import { useState } from 'react';
import { BrowserRouter as Router, Routes, Route, Link, Navigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import ChatPage from './features/chat/ChatPage';
import BlueprintsPage from './features/blueprints/BlueprintsPage';
import BlueprintDetailPage from './features/blueprints/BlueprintDetailPage';
import ProjectsPage from './features/projects/ProjectsPage';
import ProjectDetailPage from './features/projects/ProjectDetailPage';
import ErrorBoundary from './components/ErrorBoundary';
import { Button } from './components/ui/button';
import { MessageSquare, FileText, Rocket, LogOut } from 'lucide-react';
import { AUTH_KEY, clearToken } from './lib/api';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: 1,
    },
  },
});


// ---------------------------------------------------------------------------
// Login Page
// ---------------------------------------------------------------------------

function LoginPage({ onLogin }: { onLogin: () => void }) {
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    try {
      const res = await fetch('/auth/login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ password }),
      });

      if (res.ok) {
        const data = await res.json();
        if (!data.token) {
          // 旧実装は token が無いとき 'true' を置いていた。それでは
          // 「ログインした」という見た目だけが残り、API は 401 を返し続ける。
          setError('Server returned no token');
          return;
        }
        localStorage.setItem(AUTH_KEY, data.token);
        onLogin();
      } else {
        setError('Incorrect password');
      }
    } catch {
      // auth endpoint not available → check password locally via env hint
      // Fallback: accept if password matches a simple hash check
      setError('Cannot connect to auth server');
    }
  };

  return (
    <div className="flex h-screen items-center justify-center bg-gray-50">
      <div className="w-full max-w-sm space-y-6 rounded-lg border bg-white p-8 shadow-sm">
        <div className="text-center">
          <h1 className="text-2xl font-bold">Mema Pilot</h1>
          <p className="mt-1 text-sm text-muted-foreground">AI-Powered Development Platform</p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Enter password"
              className="w-full rounded-md border px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
              autoFocus
            />
          </div>
          {error && <p className="text-sm text-red-500">{error}</p>}
          <Button type="submit" className="w-full">
            Login
          </Button>
        </form>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Layout
// ---------------------------------------------------------------------------

function Layout({ children, onLogout }: { children: React.ReactNode; onLogout: () => void }) {
  return (
    <div className="flex h-screen flex-col">
      <header className="border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
        <div className="container flex h-14 items-center justify-between px-4">
          <div className="flex items-center">
            <Link to="/" className="mr-6 flex items-center space-x-2">
              <span className="font-bold">Mema Pilot</span>
            </Link>
            <nav className="flex items-center space-x-6 text-sm font-medium">
              <Link to="/chat" className="flex items-center transition-colors hover:text-foreground/80">
                <MessageSquare className="mr-2 h-4 w-4" />
                Chat
              </Link>
              <Link to="/blueprints" className="flex items-center transition-colors hover:text-foreground/80">
                <FileText className="mr-2 h-4 w-4" />
                Blueprints
              </Link>
              <Link to="/projects" className="flex items-center transition-colors hover:text-foreground/80">
                <Rocket className="mr-2 h-4 w-4" />
                Projects
              </Link>
            </nav>
          </div>
          <button
            onClick={onLogout}
            className="flex items-center gap-1 text-sm text-muted-foreground transition-colors hover:text-foreground"
          >
            <LogOut className="h-4 w-4" />
          </button>
        </div>
      </header>
      <main className="flex-1 overflow-hidden">{children}</main>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Home
// ---------------------------------------------------------------------------

function HomePage() {
  return (
    <div className="flex h-full items-center justify-center">
      <div className="mx-auto max-w-2xl space-y-6 text-center">
        <h1 className="text-4xl font-bold tracking-tight">Mema Pilot</h1>
        <p className="text-xl text-muted-foreground">
          Describe your idea, get a structured blueprint, and auto-build the project.
        </p>
        <div className="flex justify-center gap-4">
          <Link to="/chat">
            <Button size="lg">
              <MessageSquare className="mr-2 h-5 w-5" />
              Start Chat
            </Button>
          </Link>
          <Link to="/blueprints">
            <Button size="lg" variant="outline">
              <FileText className="mr-2 h-5 w-5" />
              Blueprints
            </Button>
          </Link>
          <Link to="/projects">
            <Button size="lg" variant="outline">
              <Rocket className="mr-2 h-5 w-5" />
              Projects
            </Button>
          </Link>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// App
// ---------------------------------------------------------------------------

function App() {
  const [isAuthenticated, setIsAuthenticated] = useState(
    () => !!localStorage.getItem(AUTH_KEY)
  );

  const handleLogout = () => {
    clearToken();
    setIsAuthenticated(false);
  };

  if (!isAuthenticated) {
    return (
      <ErrorBoundary>
        <LoginPage onLogin={() => setIsAuthenticated(true)} />
      </ErrorBoundary>
    );
  }

  return (
    <ErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <Router>
          <Layout onLogout={handleLogout}>
            <Routes>
              <Route path="/" element={<HomePage />} />
              <Route path="/chat" element={<ChatPage />} />
              <Route path="/blueprints" element={<BlueprintsPage />} />
              <Route path="/blueprints/:id" element={<BlueprintDetailPage />} />
              <Route path="/projects" element={<ProjectsPage />} />
              <Route path="/projects/:id" element={<ProjectDetailPage />} />
              <Route path="*" element={<Navigate to="/" />} />
            </Routes>
          </Layout>
        </Router>
      </QueryClientProvider>
    </ErrorBoundary>
  );
}

export default App;
